import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity, Clock, Users, FileText, Briefcase, Archive, BarChart2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export default function DashboardImpacto() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/dashboard/impacto');

      setData(res.data);
      // Aseguramos que los datos se rendericen de más antiguo a más reciente
      const chartDataReversed = [...res.data.chart_data].reverse();
      setData({ ...res.data, chart_data: chartDataReversed });
    } catch (err) {
      toast.error('Error al cargar métricas de impacto');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div className="spinner spinner-lg"></div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="container animate-fade-in" style={{ padding: '2rem clamp(16px, 4vw, 48px)', minHeight: '80vh' }}>
      
      {/* Header */}
      <div style={{ marginBottom: '2.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '2rem' }}>📈</span>
          <h1 className="section-title" style={{ marginBottom: 0 }}>Dashboard de Impacto</h1>
        </div>
        <span className="accent-line" />
        <p className="text-muted">
          Visualización del volumen de atenciones digitales y el ahorro de tiempo presencial.
        </p>
      </div>

      {/* Tarjetas Principales (KPIs) */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
        gap: '1.5rem', 
        marginBottom: '2.5rem' 
      }}>
        
        {/* Atenciones Totales */}
        <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid var(--primary)' }}>
          <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'rgba(0,51,102,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
            <Activity size={24} />
          </div>
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '2px', textTransform: 'uppercase' }}>Atenciones Digitales Totales</p>
            <h3 style={{ fontSize: '1.8rem', color: 'var(--text-main)', margin: 0 }}>
              {data.totals.global.toLocaleString('es-CL')}
            </h3>
          </div>
        </div>

        {/* Personas Únicas */}
        <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid #10b981' }}>
          <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
            <Users size={24} />
          </div>
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '2px', textTransform: 'uppercase' }}>Personas Atendidas (RUTs Únicos)</p>
            <h3 style={{ fontSize: '1.8rem', color: 'var(--text-main)', margin: 0 }}>
              {data.unique_people.toLocaleString('es-CL')}
            </h3>
          </div>
        </div>

        {/* Tiempo Ciudadanos */}
        <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid #f59e0b' }}>
          <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
            <Clock size={24} />
          </div>
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '2px', textTransform: 'uppercase' }}>Horas Ahorradas (Ciudadanía)</p>
            <h3 style={{ fontSize: '1.8rem', color: 'var(--text-main)', margin: 0 }}>
              {data.time_saved.citizen_hours.toLocaleString('es-CL')}
            </h3>
            <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '2px' }}>*Estimado de 2h/trámite presencial</p>
          </div>
        </div>

        {/* Tiempo Funcionarios */}
        <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid #8b5cf6' }}>
          <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'rgba(139,92,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8b5cf6' }}>
            <Briefcase size={24} />
          </div>
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '2px', textTransform: 'uppercase' }}>Horas Ahorradas (Funcionarios)</p>
            <h3 style={{ fontSize: '1.8rem', color: 'var(--text-main)', margin: 0 }}>
              {data.time_saved.staff_hours.toLocaleString('es-CL')}
            </h3>
            <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '2px' }}>*Estimado de 30m/trámite digital</p>
          </div>
        </div>

      </div>

      {/* Desglose y Gráficos */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem', alignItems: 'stretch' }}>
        
        {/* Desglose por Módulo */}
        <div className="glass-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
            <BarChart2 size={20} color="var(--primary)" />
            <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--primary-dark)' }}>Desglose por Módulo</h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1, justifyContent: 'center' }}>
            
            <div style={{ background: 'var(--bg-body)', padding: '1rem', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileText size={18} color="var(--primary)" />
                <span style={{ fontWeight: 600 }}>Solicitudes</span>
              </div>
              <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>{data.totals.solicitudes.toLocaleString('es-CL')}</span>
            </div>

            <div style={{ background: 'var(--bg-body)', padding: '1rem', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Activity size={18} color="#10b981" />
                <span style={{ fontWeight: 600 }}>Trámites</span>
              </div>
              <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>{data.totals.tramites.toLocaleString('es-CL')}</span>
            </div>

            <div style={{ background: 'var(--bg-body)', padding: '1rem', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Archive size={18} color="#f59e0b" />
                <span style={{ fontWeight: 600 }}>Archivero</span>
              </div>
              <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>{data.totals.archiveros.toLocaleString('es-CL')}</span>
            </div>

          </div>
        </div>

        {/* Gráfico Mensual */}
        <div className="glass-card" style={{ display: 'flex', flexDirection: 'column', minHeight: '350px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
            <Activity size={20} color="var(--primary)" />
            <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--primary-dark)' }}>Evolución (Últimos 6 Meses)</h3>
          </div>
          
          <div style={{ flex: 1, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.chart_data}
                margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <Tooltip 
                  cursor={{ fill: 'rgba(0,51,102,0.05)' }}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                />
                <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                <Bar dataKey="Solicitudes" stackId="a" fill="var(--primary)" radius={[0, 0, 4, 4]} />
                <Bar dataKey="Trámites" stackId="a" fill="#10b981" />
                <Bar dataKey="Archiveros" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

    </div>
  );
}
